import { GraphQLFieldConfig, GraphQLNonNull } from 'graphql'
import { Context } from '../shared'

import { ContrastQueryParameters } from '../endpoint-helpers'
import {
  AttackEventsPage,
  attackEventsPageType
} from '../types/attack-events-page'
import {
  attackEventPagingArgs,
  AttackEventPagingArgs
} from '../types/attack-event-paging-args'
import { appendAttackEventFiltersToBody } from '../endpoint-helpers/attack-events'
import {
  AttackEventOrder,
  AttackEventOrderField
} from '../types/attack-event-order'
import { normalizeApplicationLanguage } from './helpers/application'
import { transformRole } from './helpers/role'
import { AttackEvent } from '../types/attack-event'
import { normalizeSourceName } from './source-name'

const orderMap: { [key in AttackEventOrderField]: string } = {
  NAME: 'protectionRule.name',
  SOURCE_IP: 'agentMessageLeft.displaySourceIP',
  TYPE: 'type',
  TIME: 'timestamp',
  URI: 'uri',
  RULE_NAME: 'protectionRule.name',
  SOURCE_NAME: 'sourceName'
}

export function appendSortToParams(
  params: ContrastQueryParameters,
  orderBy: AttackEventOrder | undefined
) {
  if (orderBy) {
    if (orderBy.field === 'SOURCE_NAME') {
      // TODO remove this condition when endpoint allows this sort option
      return
    }
    const { field, direction } = orderBy
    params.append(
      'sort',
      `${direction === 'DESC' ? '-' : ''}${orderMap[field]}`
    )
  }
}

export const normalizeAttackEvent: (event) => AttackEvent = ({
  event_uuid: id,
  received,
  source: sourceIP,
  result,
  displayName,
  type,
  rule_uuid: ruleUuid,
  suppressed,
  application,
  server,
  user_input: userInput,
  detectionType,
  details: eventDetails,
  httpRequest,
  callLocation,
  url,
  attack_type: attackType,
  attackers,
  sourceName,
  tags,
  exclusion_created: hasExclusion
}) => {
  const parsedEventDetails = eventDetails ? JSON.parse(eventDetails) : undefined
  return {
    id,
    received,
    sourceIP,
    result,
    displayName,
    type,
    ruleUuid,
    suppressed,
    application: {
      id: application.app_id,
      name: application.name,
      language: normalizeApplicationLanguage(application.language),
      role: application.roles ? transformRole(application.roles) : undefined
    },
    server: {
      id: server.server_id,
      name: server.name,
      environment: server.environment
    },
    userInput: userInput
      ? {
          ...userInput,
          omittedValue: userInput.omitted_value,
          truncatedValue: userInput.truncated_value
        }
      : undefined,
    details: parsedEventDetails
      ? {
          findings: Array.isArray(parsedEventDetails.findings)
            ? parsedEventDetails.findings
            : undefined,
          entryName:
            typeof parsedEventDetails.entryName === 'string'
              ? parsedEventDetails.entryName
              : undefined,
          query:
            typeof parsedEventDetails.query === 'string'
              ? parsedEventDetails.query
              : undefined,
          command:
            typeof parsedEventDetails.command === 'string'
              ? parsedEventDetails.command
              : undefined,
          path:
            typeof parsedEventDetails.path === 'string'
              ? parsedEventDetails.path
              : undefined,
          /**
           * Some Details DTMs use start while some use startIndex
           * but they refer to the same thing
           */
          start:
            typeof parsedEventDetails.start === 'number'
              ? parsedEventDetails.start
              : typeof parsedEventDetails.startIndex === 'number'
              ? parsedEventDetails.startIndex
              : undefined,
          /**
           * Some Details DTMs use end while some use endIndex
           * but they refer to the same thing
           */
          end:
            typeof parsedEventDetails.end === 'number'
              ? parsedEventDetails.end
              : typeof parsedEventDetails.endIndex === 'number'
              ? parsedEventDetails.endIndex
              : undefined,
          commandArgs: Array.isArray(parsedEventDetails.command_args)
            ? parsedEventDetails.command_args
            : undefined,
          directory:
            parsedEventDetails.directory &&
            parsedEventDetails.directory.length > 0
              ? parsedEventDetails.directory
              : undefined
        }
      : undefined,
    detectionType,
    httpRequest: httpRequest
      ? {
          uri: httpRequest.uri,
          method: httpRequest.method,
          queryString: httpRequest.qs,
          version: httpRequest.version,
          body: httpRequest.body,
          headers: httpRequest.headers,
          parameters: httpRequest.parameters,
          valid: httpRequest.valid
        }
      : undefined,
    callLocation,
    url,
    attackType,
    attackers,
    sourceName: sourceName ? normalizeSourceName(sourceName) : null,
    tags: !!tags ? tags : [],
    hasExclusion
  }
}

export const attackEvents: GraphQLFieldConfig<
  any,
  Context,
  AttackEventPagingArgs
> = {
  description: 'Get a page of attack events',
  type: new GraphQLNonNull(attackEventsPageType),
  args: attackEventPagingArgs,
  async resolve(_source, args, context): Promise<Partial<AttackEventsPage>> {
    const { offset, limit, organizationID, orderBy, filters } = args

    const params = new ContrastQueryParameters()
    params.append('offset', offset)
    params.append('limit', limit)
    appendSortToParams(params, orderBy)
    params.append('expand', 'skip_links,sourceName,tags,application_roles')

    const { events = [], count: total } = await context.connector.post(
      `/${organizationID}/rasp/events/new?${params}`,
      appendAttackEventFiltersToBody(filters)
    )

    return {
      total,
      items: events.map(normalizeAttackEvent)
    }
  }
}
